import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import type { AccentName } from "../../lib/accents";
import type { ClayArtName } from "../../lib/clayArt";
import { ClayArt } from "../ClayArt";

interface EmptyStateProps {
  /** Small icon, used when there is no `art`. */
  icon?: LucideIcon;
  /** A clay illustration (lib/clayArt.ts) instead of the icon — the friendlier choice for "nothing here yet". */
  art?: ClayArtName;
  /** Hue of the glow behind the icon or art. Default `sky`. */
  accent?: AccentName;
  title: string;
  description?: string;
  action?: ReactNode;
  /** `sm` for an empty list inside a section; `md` (default) for a page. */
  size?: "sm" | "md";
  className?: string;
}

// The soft glow behind the icon (literal classes).
const ICON_GLOW: Record<AccentName, string> = {
  sky: "from-sky-100 to-violet-100 text-sky-700",
  rose: "from-rose-100 to-amber-100 text-rose-700",
  amber: "from-amber-100 to-rose-100 text-amber-700",
  emerald: "from-emerald-100 to-sky-100 text-emerald-700",
  violet: "from-violet-100 to-sky-100 text-violet-700",
  indigo: "from-indigo-100 to-sky-100 text-indigo-700",
};

/**
 * "Nothing here yet", said warmly (docs/v2-core/STYLE.md): open and centred
 * on the page's scene — no dashed box — with a clay illustration (or a round
 * icon pebble) on a soft glow, a Fredoka title, one calm line and one action.
 * Also fine for a load error with a retry action (use the icon, not art).
 */
export function EmptyState({
  icon: Icon,
  art,
  accent = "sky",
  title,
  description,
  action,
  size = "md",
  className,
}: EmptyStateProps) {
  const small = size === "sm";
  return (
    <div
      className={cn(
        "flex flex-col items-center text-center",
        small ? "px-4 py-6" : "px-6 py-10 sm:py-14",
        className,
      )}
    >
      {art ? (
        <ClayArt name={art} accent={accent} className={cn("mb-4", small ? "w-24" : "w-32 sm:w-40")} />
      ) : (
        Icon && (
          <span
            className={cn(
              "mb-4 inline-flex items-center justify-center rounded-full bg-gradient-to-br shadow-soft ring-8 ring-white/60",
              small ? "h-12 w-12" : "h-16 w-16",
              ICON_GLOW[accent],
            )}
          >
            <Icon className={small ? "h-6 w-6" : "h-7 w-7"} aria-hidden="true" />
          </span>
        )
      )}
      <h3
        className={cn(
          "text-balance font-display font-semibold text-neutral-900",
          small ? "text-lg" : "text-xl sm:text-2xl",
        )}
      >
        {title}
      </h3>
      {description && (
        <p className="mx-auto mt-1.5 max-w-sm text-pretty text-sm text-neutral-600 sm:text-[15px]">
          {description}
        </p>
      )}
      {action && <div className="mt-5 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  );
}
